import i18n from 'i18next';
// Localises known notification types on the client so in-app notifications can
// render in the viewer's language (sv/en) via i18next, instead of only showing
// the Swedish text generated and stored server-side. Unknown event types fall
// back to the stored title/body unchanged.

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface LocalizableNotification {
  eventKey?: string | null;
  title: string;
  body: string;
  metadata?: Record<string, unknown> | null;
}

export const localizeNotification = (
  n: LocalizableNotification,
  t: Translate,
): { title: string; body: string } => {
  if (['payment.funding_required', 'payment.funding_reminder'].includes(n.eventKey ?? '') && typeof n.metadata?.amountOre === 'number') {
    const prefix = n.eventKey === 'payment.funding_reminder' ? 'reminder' : 'required';
    return {
      title: t(`fundingNotice.${prefix}Title`),
      body: t(`fundingNotice.${prefix}Body`, {
        contractNumber: n.metadata.contractNumber,
        projectDescription: n.metadata.projectDescription,
        amount: new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'SEK' }).format(n.metadata.amountOre / 100)
      })
    };
  }
  if (['payout.reversed', 'payout.failed'].includes(n.eventKey ?? '') &&
      n.metadata?.adyenTransferStatus === 'refused' && n.metadata?.adyenTransferReason === 'notEnoughBalance') {
    return { title: t('payoutAvailability.title'), body: t('payoutAvailability.notificationBody') };
  }
  // A contract the customer sent back with an objection (ClickUp 869fcmr7k):
  // the objection is shown in full. The same event key is also used for a
  // declined signing, which keeps its stored text.
  if (n.eventKey === 'contract.draft_rejected' && n.metadata?.returnedByCustomer === true) {
    const comment = typeof n.metadata.comment === 'string' ? n.metadata.comment.trim() : '';
    return {
      title: t('contractNotice.returnedTitle'),
      body: comment
        ? t('contractNotice.returnedBodyComment', { contractNumber: n.metadata.contractNumber, comment, interpolation: { escapeValue: false } })
        : t('contractNotice.returnedBody', { contractNumber: n.metadata.contractNumber })
    };
  }
  // "Be beställaren attestera" (ClickUp 869fnf0e5): the notification asks for
  // the attestation; a request about a change keeps its stored text.
  if (n.eventKey === 'milestone.message_posted' && n.metadata?.attestationRequest === true &&
      typeof n.metadata.milestoneNumber === 'number') {
    const preview = typeof n.metadata.messagePreview === 'string' ? n.metadata.messagePreview.trim() : '';
    return {
      title: t('milestoneNotice.attestationRequestTitle', { number: n.metadata.milestoneNumber }),
      body: preview
        ? t('milestoneNotice.attestationRequestBody', { preview, interpolation: { escapeValue: false } })
        : t('milestoneNotice.attestationRequestBodyEmpty', { number: n.metadata.milestoneNumber })
    };
  }
  if (n.eventKey === 'payout.booked') {
    const isFinal =
      n.metadata?.paymentKind === 'final' || n.metadata?.isFinalPayout === true;
    const label = isFinal
      ? t('projectView.transactions.notifications.payoutFinal')
      : t('projectView.transactions.notifications.payoutPartial');
    const payee =
      (typeof n.metadata?.payeeCompanyName === 'string' &&
        n.metadata.payeeCompanyName) ||
      t('projectView.transactions.notifications.payeeFallback');
    return {
      title: label,
      body: t('projectView.transactions.notifications.payoutBody', {
        label,
        payee,
      }),
    };
  }
  return { title: n.title, body: n.body };
};
